/**
 * Hub connection helpers shared by the Servers page and the `/tab-connect`
 * boot route (new Electron tabs auto-connect to their server).
 *
 * The route is **resolved, not chosen** (plan `docs/plans/LOCAL-FIRST-CONNECT.md`
 * §2/D2, and the owner's words: *"the local detection should happen automatically
 * at each connection … before establish try local"*): a hub the account knows and
 * can reach at its LAN address is opened there, by a **claim** with the app's
 * cloud token (`services/hubLocalFirst.ts` — the hub verifies it cloud-side);
 * only when the LAN address does not answer, or the claim refuses, does the
 * tunnel take the session, and that fallback is stated once
 * (`services/hubRouteNotice.ts`). A hub this device holds a **device credential**
 * for (a panel, an installer's device) opens with it, exactly as before.
 *
 * No dialogs: a caller whose silent path fails is told so, and the surfaces keep
 * their own UI fallbacks around it.
 */
import {
  buildHubApiUrl,
  getHubOwnKeys,
  getRuntimeConfig,
  rememberTabActiveHub,
  setActiveHub,
  HUB_TLS_PORT,
} from '../config/runtime';
import { cloudResumePath, localResumePath } from '../utils/hubResumePath';
import { hasStoredDeviceCredential } from './hubBinding';
import {
  localRefusalFallsBackToTunnel,
  openLocalFirstSession,
  resolveLocalRoute,
  type LocalRouteRefusal,
} from './hubLocalFirst';
import { stateRouteFallback } from './hubRouteNotice';
import { rememberLastRoute } from './serverProfiles';
import { serverConnectPreference } from './serverRoutePreference';
import { apiFetch } from './api';
import { errorCode, errorMessage, errorStatus } from '../utils/errors';
import { hubOpenMessageKey } from '../utils/hubOpenFallback';
import { classifyCloudSessionRefusal, type CloudSessionRefusal } from './cloudSessionRefusal';
import { useAuthStore } from '../stores/auth';
import { useWebRTCStore } from '../stores/webrtc';
import { useDashboardStore } from '../stores/dashboard';
import { useEntitiesStore } from '../stores/entities';
import { useDeviceAutomationsStore } from '../stores/deviceAutomations';

export interface HubTarget {
  mode: 'local' | 'cloud';
  ip?: string;
  port?: number;
  hubId?: string;
  name?: string;
  /**
   * The **account's** id for the hub (`GET /hubs` → `hubId`), when the caller
   * knows it apart from `hubId`: the local-first route reads the account's
   * `hubPublicId` under it (`services/hubLocalFirst.ts`). Omitted means "the same
   * row as `hubId`", which is what a launcher card holds.
   */
  accountHubId?: string;
  /**
   * The scheme this address was reached on — `true` = TLS, `false` = cleartext,
   * `undefined` = nothing said (`HubSecureHint`, card `t_cf6ea0e5`). The tab
   * route carries it as `?secure=1` (`electron/tabs.ts`), and every local
   * connect path threads it into `buildHubApiUrl`/`loginToHub` so a hub that
   * answers over TLS is dialled over TLS.
   */
  secure?: boolean;
}

/**
 * The scheme to remember for a window/tab: a measured boolean wins, and only an
 * address whose scheme nobody knows falls back to this app's own rule (443 IS
 * TLS — `ws://host:443` can never connect).
 */
function tabScheme(target: Pick<HubTarget, 'secure'>, port: number): boolean {
  return typeof target.secure === 'boolean' ? target.secure : port === HUB_TLS_PORT;
}

export interface SilentConnectResult {
  ok: boolean;
  error?: string;
  /**
   * How the session was opened, when it was (plan §2/D2/D5): `session` — this
   * window was already on the hub locally; `local` — the LAN address answered
   * and the claim minted the session; `device` — the device credential this
   * device holds (a panel, an installer's device); `cloud` — the tunnel took it
   * (`connectResolvingRoute`).
   */
  route?: 'session' | 'local' | 'device' | 'cloud';
  /**
   * The LAN route did not open this hub — nothing answered at its address, the
   * responder was a different hub, it could not prove it holds the key the
   * account pinned (plan §2/D6), or the claim refused — so the tunnel is the
   * fallback (§2/D2 step 3). Only a caller that holds a cloud route may take it;
   * `connectResolvingRoute` does, and states it once.
   */
  tunnelFallback?: boolean;
  /** Why the LAN door was not used, for the log and the surface's sentence. */
  localRefusal?: LocalRouteRefusal | null;
  /**
   * Which of the server's **own settings** decided this route (plan §2/C4, card
   * `t_16b7704a`): `'cloud-only'` — the switch pinned it to the tunnel and the
   * LAN address was never dialled; `'manual'` — the stored address was the one
   * tried first; `null` — neither, i.e. today's automatic local-first.
   */
  preference?: 'cloud-only' | 'manual' | null;
  /**
   * The LAN address the route actually used, when one opened the session: the
   * stored manual address for a `'manual'` server, else the caller's. A caller
   * that hands the hub to another surface (a shell tab) passes this instead of
   * the address it meant to dial.
   */
  lanAddress?: { ip: string; port: number; secure?: boolean } | null;
  /**
   * The cloud's own refusal of the tunnel session, **by name**
   * (`services/cloudSessionRefusal.ts`): `remote_access_not_licensed` — the hub is
   * set to local access only, so the tunnel is not a route to it (plan
   * `FLOOR-PLAN-AND-PLAN-EDITOR.md` §3.2, D3). A named refusal, never a boolean;
   * `null`/absent means the cloud did not refuse by name.
   */
  cloudRefusal?: CloudSessionRefusal | null;
  /**
   * The refusal's own sentence, as an i18n key: the hub's claim refusal
   * (`auth.claim.*`) or the cloud's own (`hub.open.failure.remote_access_not_licensed`
   * — `hubOpenMessageKey`). `null` means the refusal has no sentence of its own and
   * the surface falls back to `error`.
   */
  messageKey?: string | null;
  /**
   * Fresh hub with no users — the setup wizard must run.
   *
   * No local producer any more (card `t_28677589`): the flag was set by the
   * hub-password login, and a hub with no users at all is a hub that answers
   * `claimable:true` — its way in is adoption (`resolveDiscoveredHubPlan`), not
   * a first-user form filled in by hand. Kept because callers still branch on it
   * for the cloud path's own answers.
   */
  needsSetup?: boolean;
}

/** Parse `ip:port` out of any hub API URL (otto-local://, http://, https://). */
export function parseHubApiUrl(apiUrl: string): { ip: string; port: number } | null {
  try {
    const parsed = new URL(apiUrl);
    const port = Number(parsed.port || (parsed.protocol === 'https:' ? 443 : 80));
    if (!parsed.hostname || !Number.isFinite(port)) return null;
    return { ip: parsed.hostname, port };
  } catch {
    return null;
  }
}

/**
 * The position this window was on inside the hub, read straight from storage —
 * no session and no request. `resolveLocalResumePath` is the sibling that
 * VERIFIES a room against the hub, which takes a live bearer; a caller holding a
 * dead one (`stores/connectionLost.ts` — the session has just expired) reads the
 * stored value here, before its own `logout()` clears it, and navigates to it
 * once the new session is up.
 */
export function storedLocalResumePath(): string | null {
  try {
    return localResumePath(getRuntimeConfig().apiUrl);
  } catch {
    return null;
  }
}

/**
 * Connect to a hub at its LAN address (plan §2/D2, §2/D5) — the route is
 * resolved here, in this order:
 *
 *   1. **already local** — this window is on the hub and holds its session: open
 *      (nothing is dialled, nothing is claimed);
 *   2. **the account's own token** — the hub is one the account knows and answers
 *      at that address with its own `publicId`: `POST <lan>/api/auth/claim`
 *      mints the session (`services/hubLocalFirst.ts`), and it is what makes
 *      every LATER connect local without a cloud round trip;
 *   3. **the device credential** (F5) — the door for a panel or an installer's
 *      device, which has no account and cannot claim;
 *   4. otherwise **nothing here opens it**, and the answer says why
 *      (`localRefusal`) and whether the tunnel may take over (`tunnelFallback`).
 *
 * The stored-credential path that used to be here (`resolveStoredCredentials` →
 * `loginToHub`, i.e. a hub username + password a person once typed, replayed
 * silently) is gone with the rest of the hub-password sign-in path (card
 * `t_28677589`). A saved sign-in is not a credential this device holds, and on
 * this fleet it is usually dead by construction: the hub's `POST /api/auth/login`
 * is gated to `authKind === 'legacy'` rows, while the rows a hub actually uses
 * are `cloud`/`bootstrap`.
 *
 * Returns `{ ok:true }` when the session is live (or already was).
 */
export async function connectLocalSilently(target: HubTarget): Promise<SilentConnectResult> {
  const authStore = useAuthStore();
  const webrtcStore = useWebRTCStore();

  // ── The server's own settings, read before anything is dialled (§2/C4) ────
  const preference = serverConnectPreference({
    cloudId: target.accountHubId ?? null,
    hubId: target.hubId ?? null,
  });

  // A `cloudOnly` server is **never** dialled on the LAN — not as a first try and
  // not as a probe (the card's own rule: a connect that still probes is a failed
  // card). This is the one function that dials, so the promise is kept here; the
  // tunnel is the route, and `connectResolvingRoute` spends it.
  if (preference.kind === 'cloud-only') {
    console.info('[HubConnect] cloud-only: the LAN address is not dialled', {
      hub: target.name || target.hubId || `${target.ip}:${target.port}`,
    });
    return {
      ok: false,
      error: 'This server is set to cloud only — it is reached through the cloud',
      localRefusal: null,
      tunnelFallback: false,
      preference: 'cloud-only',
    };
  }

  // A stored manual address **is** the address this device dials for that hub
  // (§2/C4 item 4): it exists for the servers discovery cannot see at all — the
  // owner's VPN case — so it is used whether or not the caller named one, and it
  // is dialled with no scheme hint (which rung answers is the ladder's
  // measurement, never a stored string's punctuation).
  const manual = preference.kind === 'manual' ? { ip: preference.address.host, port: preference.address.port } : null;
  const ip = manual?.ip ?? target.ip;
  const port = manual?.port ?? target.port;
  const secure = manual ? undefined : target.secure;
  const preferenceKind: 'manual' | null = manual ? 'manual' : null;

  if (!ip || !port) return { ok: false, error: 'Missing hub address', preference: preferenceKind };

  // Already authenticated to this hub over the LOCAL transport? A cloud/WebRTC
  // session to another hub keeps `config.apiUrl` pointing at the last local hub,
  // so matching the URL alone is only valid without an active WebRTC session.
  if (authStore.isAuthenticated && !webrtcStore.connected && !webrtcStore.connecting) {
    const config = getRuntimeConfig();
    if (config.apiUrl === buildHubApiUrl(ip, port, secure)) {
      // A reused session still pins THIS window to its hub (per-tab reload
      // memory — the shared localStorage may point at another hub's tab).
      rememberTabActiveHub({ ip, port, secure: tabScheme({ secure }, port), hubId: target.hubId, name: target.name });
      return { ok: true, route: 'session', preference: preferenceKind, lanAddress: { ip, port, secure } };
    }
  }

  // ── The LAN door: the account's own token, verified by the hub (§2/D1/D2) ──
  let localRefusal: LocalRouteRefusal = 'no-cloud-session';
  let messageKey: string | null = null;
  if (authStore.isCloudAuthenticated) {
    // A token about to expire is refreshed BEFORE it is carried to the hub: the
    // hub validates it cloud-side, and a stale bearer is a refusal about the
    // wrong thing (`invalid_cloud_token`).
    await authStore.ensureCloudToken().catch(() => false);

    const verdict = await resolveLocalRoute(
      {
        ip,
        port,
        secure,
        hubId: target.hubId ?? null,
        accountHubId: target.accountHubId ?? target.hubId ?? null,
        name: target.name ?? null,
      },
      { cloudAccessToken: authStore.cloudAccessToken },
    );

    if (verdict.ok) {
      // Switching to a local hub must always tear down a stale remote cloud
      // WebRTC session first: with one live, local API calls can be misrouted
      // through the remote transport layer.
      if (webrtcStore.connected || webrtcStore.connecting) webrtcStore.disconnect();
      // Pin THIS window at the address the session BELONGS to — the rung that
      // proved the key, which is not always the address the caller named: the
      // ladder ends at the hub's own raw API port, and a stand-in on the caller's
      // port must not become this window's hub (card `t_2072e0e3`). The session
      // is filed under this window's hub (`stores/auth.ts` → `tabHubTokenKey`),
      // so the pin comes first — and the pin is the proof's address.
      rememberTabActiveHub({
        ip: verdict.endpoint.ip,
        port: verdict.endpoint.port,
        secure: verdict.endpoint.secure,
        hubId: verdict.hubId ?? target.hubId,
        name: verdict.name ?? target.name,
      });
      await openLocalFirstSession(verdict);
      // The stores that belong to the hub being left (the same three
      // `connectCloudSilently` clears): a switch must not serve the previous
      // hub's dashboards, entities or device automations.
      useDashboardStore().resetForHubSwitch();
      useEntitiesStore().resetForHubSwitch();
      useDeviceAutomationsStore().resetForHubSwitch();
      console.info('[HubConnect] local route opened by claim', {
        hub: `${verdict.endpoint.ip}:${verdict.endpoint.port}`,
        publicId: verdict.publicId,
        role: verdict.account.role,
      });
      // The route that actually opened this hub, and the address that answered
      // for it, are the device's own facts about the account's server (plan
      // §2/C2, card `t_f4f925f9`): remembered at the proof's address — the rung
      // that proved the key, not the address the caller named (card `t_2072e0e3`).
      rememberLastRoute(
        { cloudId: target.accountHubId ?? null, hubId: target.hubId ?? verdict.hubId ?? null },
        {
          route: 'local',
          host: verdict.endpoint.ip,
          port: verdict.endpoint.port,
          secure: verdict.endpoint.secure,
        },
      );
      return {
        ok: true,
        route: 'local',
        preference: preferenceKind,
        lanAddress: { ip: verdict.endpoint.ip, port: verdict.endpoint.port, secure: verdict.endpoint.secure },
      };
    }

    localRefusal = verdict.reason;
    messageKey = verdict.messageKey;
  }

  // The tunnel is the fallback only where the LAN address WAS the question and
  // the account can reach the hub some other way: a hub it cannot name has no
  // tunnel either, and neither has a device with no cloud session. The rule
  // itself lives in `hubLocalFirst` — next to the refusals it is about — so
  // "does the tunnel take this over?" is a unit test, and a new refusal cannot
  // silently keep the session on the floor.
  const tunnelFallback = localRefusalFallsBackToTunnel(localRefusal);

  // ── The device credential (a panel, an installer's device) ────────────────
  // The one credential that opens a hub with nothing typed and no account. The
  // hub id (when the caller has it) and the endpoint spelling are both asked: a
  // credential enrolled over the cloud lives under `hub:<hubId>`, one enrolled
  // over the LAN under this connection's endpoint key.
  if (!hasStoredDeviceCredential({ ip, port, secure, hubId: target.hubId ?? null })) {
    return {
      ok: false,
      error: 'No credential for this hub — join or adopt it',
      tunnelFallback,
      localRefusal,
      messageKey,
      preference: preferenceKind,
    };
  }

  // Switching to a local hub must always tear down a stale remote cloud WebRTC
  // session first: with one live, local API calls can be misrouted through the
  // remote transport layer.
  if (webrtcStore.connected || webrtcStore.connecting) webrtcStore.disconnect();

  try {
    // Point this window (and this tab) at the hub. The credential does not need
    // to be re-stored: it is already the connection's own, and
    // `services/apiCredential.ts` is what puts it on each request — this window
    // holds no user JWT for a hub it opens as a device.
    setActiveHub(ip, port, secure);
    if (target.hubId) {
      // Per-hub UI state keys off the hub's own id when it is known, so it
      // survives an address change (the same upgrade `loginToHub` used to do).
      localStorage.setItem('otto_active_hub_key', `hub:${target.hubId}`);
    }
    rememberTabActiveHub({ ip, port, secure: tabScheme({ secure }, port), hubId: target.hubId, name: target.name });
  } catch (error) {
    // No runtime config yet (a very early boot) or a storage that refuses: the
    // caller has to know, because every later request resolves its hub from this.
    return { ok: false, error: errorMessage(error, 'Could not pin this hub') };
  }

  // The stores that belong to the hub being left (the same three
  // `connectCloudSilently` clears): a switch must not serve the previous hub's
  // dashboards, entities or device automations.
  useDashboardStore().resetForHubSwitch();
  useEntitiesStore().resetForHubSwitch();
  useDeviceAutomationsStore().resetForHubSwitch();

  return { ok: true, route: 'device', preference: preferenceKind, lanAddress: { ip, port, secure } };
}

/**
 * Open a hub whose route the caller does not know — the way every connect entry
 * point should ask for it (§2/D2/D5).
 *
 * The server's **own settings decide first** (plan §2/C4, card `t_16b7704a`):
 *
 *   1. a `cloudOnly` server is pinned to the tunnel — the LAN address is not
 *      dialled at all, and because that LAN address was never the question, the
 *      route is not announced as a fallback either;
 *   2. a `manual` address is the one dialled first (the hub discovery cannot
 *      see), the tunnel still the second half of the pair;
 *   3. otherwise the LAN address is tried first (`connectLocalSilently`, which
 *      claims with the account's token), and the tunnel takes the session when
 *      it did not open the hub **and** the caller holds a cloud route (`hubId`)
 *      — either because that LAN address was asked and did not answer, or
 *      because there is no LAN address to ask at all (a hub of the account only
 *      the tunnel can reach). The fallback is stated once per hub per session
 *      (`services/hubRouteNotice.ts`) **only where the LAN address was the
 *      question**: a hub that never had one is not news.
 */
export async function connectResolvingRoute(target: HubTarget): Promise<SilentConnectResult> {
  const preference = serverConnectPreference({
    cloudId: target.accountHubId ?? null,
    hubId: target.hubId ?? null,
  });

  // ── 1. Cloud only: the tunnel is the route (plan D2's switch) ─────────────
  // Nothing of the LAN door runs — no identity read, no claim, no probe — and
  // the result says so: `tunnelFallback` stays false because no LAN answer was
  // ever asked for, so no surface paints the "fell back to the tunnel" line.
  if (preference.kind === 'cloud-only') {
    const authStore = useAuthStore();
    if (!target.hubId || !authStore.isCloudAuthenticated) {
      return {
        ok: false,
        error: 'This server is set to cloud only — it is reached through the cloud account',
        localRefusal: null,
        tunnelFallback: false,
        preference: 'cloud-only',
      };
    }
    const cloud = await connectCloudSilently({ mode: 'cloud', hubId: target.hubId, name: target.name });
    if (!cloud.ok) return { ...cloud, preference: 'cloud-only', tunnelFallback: false };
    console.info('[HubConnect] cloud-only server: the tunnel is the route', {
      hub: target.name || target.hubId,
    });
    return { ok: true, route: 'cloud', tunnelFallback: false, localRefusal: null, preference: 'cloud-only' };
  }

  // ── 2/3. The LAN door, with the address the settings ask for ──────────────
  // A `manual` address counts as an address even when the caller has none (the
  // VPN case): `connectLocalSilently` spends it from the record itself.
  const hasLanAddress = preference.kind === 'manual' || (!!target.ip && !!target.port);
  const local = hasLanAddress
    ? await connectLocalSilently(target)
    : ({ ok: false, error: 'Missing hub address' } as SilentConnectResult);
  if (local.ok) return local;

  const authStore = useAuthStore();
  if (!target.hubId || !authStore.isCloudAuthenticated) return local;

  // The LAN address WAS the question and did not answer (`tunnelFallback`), or
  // there was none to ask. A hub the account cannot name has neither route.
  const lanWasTheQuestion = local.tunnelFallback === true;
  if (!lanWasTheQuestion && hasLanAddress) return local;

  const cloud = await connectCloudSilently({ mode: 'cloud', hubId: target.hubId, name: target.name });
  if (!cloud.ok) {
    // Both doors are shut. When the cloud refused **by name**, that name is the
    // fact the person needs — `remote_access_not_licensed`: the hub is set to local
    // access only, so the tunnel was never a route to it (plan §3.2/D3) — and it is
    // also the reason there is no fallback left, which is why its sentence is the
    // one a surface paints. The LAN route's own word (`localRefusal`) and its claim
    // sentence stay on the value for the log and for a surface that words both.
    return {
      ...local,
      cloudRefusal: cloud.cloudRefusal ?? null,
      messageKey: cloud.messageKey ?? local.messageKey ?? null,
    };
  }

  if (lanWasTheQuestion) {
    stateRouteFallback({
      hubId: target.hubId,
      hubName: target.name || target.hubId,
      // One more reason a LAN route may not answer, with its own sentence (card
      // `t_4623769a`): the browser is holding the request for its
      // `local-network-access` permission. It is stated like the others — once
      // per hub per session — and it is the case where the person most needs to
      // know *why*, because allowing the permission is what makes the LAN route
      // work at all.
      reason: local.localRefusal === 'permission-held' ? 'permission' : 'tunnel',
    });
  }
  console.info('[HubConnect] on the tunnel', {
    hub: hasLanAddress ? `${target.ip}:${target.port}` : target.hubId,
    reason: local.localRefusal ?? 'no-lan-address',
  });
  return {
    ok: true,
    route: 'cloud',
    tunnelFallback: lanWasTheQuestion,
    localRefusal: local.localRefusal ?? null,
    preference: local.preference ?? null,
    lanAddress: local.lanAddress ?? null,
  };
}

/** Connect to a cloud hub (WebRTC) using the cloud session. */
export async function connectCloudSilently(target: HubTarget): Promise<SilentConnectResult> {
  const authStore = useAuthStore();
  const webrtcStore = useWebRTCStore();
  const hubId = target.hubId;
  if (!hubId) return { ok: false, error: 'Missing hub id' };

  if (!authStore.cloudAccessToken && !authStore.isCloudAuthenticated) {
    return { ok: false, error: 'Not signed in to the cloud account' };
  }

  try {
    const tokenValid = await authStore.ensureCloudToken();
    if (!tokenValid) return { ok: false, error: 'Cloud session expired' };

    if (webrtcStore.connected && webrtcStore.hubId === hubId) return { ok: true };

    useDashboardStore().resetForHubSwitch();
    useEntitiesStore().resetForHubSwitch();
    // Device automation caches are keyed by deviceId (collides across hubs
    // after a backup restore) — clear them so the hub re-fetches.
    useDeviceAutomationsStore().resetForHubSwitch();
    // Cancel a background reconnect for the same hub — without this,
    // connectToHub silently no-ops and navigation runs before it is live.
    if (webrtcStore.connecting && !webrtcStore.connected && webrtcStore.hubId === hubId) {
      webrtcStore.disconnect();
    }
    await webrtcStore.connectToHub(hubId, authStore.cloudAccessToken || '');
    // The tunnel is the route that opened this hub (plan §2/C2, card
    // `t_f4f925f9`) — the device's own fact about the account's server.
    rememberLastRoute({ cloudId: target.accountHubId ?? null, hubId: target.hubId ?? null }, { route: 'cloud' });
    return { ok: true };
  } catch (error) {
    // A refusal the cloud **names** is a fact of its own
    // (`services/cloudSessionRefusal.ts`): `remote_access_not_licensed` is not
    // "the connection failed" — the hub's plan terms are local-only, and the
    // tunnel is not a route to it. The name travels on the value and the sentence
    // is the vocabulary's (`hub.open.failure.*`, all six catalogues), never the
    // wire's own words about an internal state.
    const refusal = classifyCloudSessionRefusal(errorStatus(error) ?? null, { code: errorCode(error) });
    return {
      ok: false,
      error: errorMessage(error, 'Failed to connect to hub'),
      cloudRefusal: refusal,
      messageKey: refusal ? hubOpenMessageKey(refusal) : null,
    };
  }
}

/**
 * Resume path for a cloud hub: the last visited path for THIS hub, avoiding
 * `cameras` (a re-entering burst of snapshot requests makes the connect feel
 * broken) and falling back to the dashboards.
 *
 * The keys must be this hub's own (`getHubOwnKeys`): walking every key
 * `getHubSessionKeys()` returns also yields `otto_active_hub_key` — the hub the
 * client is leaving — so connecting to a hub with no stored position of its own
 * resumed the other hub's page and an id-bearing path (`users/<id>`) opened
 * against the wrong hub and rendered "not found".
 */
export function getHubResumePath(hubId: string): string {
  return cloudResumePath(hubId);
}

/**
 * Resume path for a local hub (last path from per-hub storage, with a
 * room-validity check because specific room ids can disappear).
 */
export async function resolveLocalResumePath(): Promise<string> {
  let target = '/rooms/overview';
  try {
    const apiUrl = getRuntimeConfig().apiUrl;
    const lastPath = localResumePath(apiUrl);

    if (lastPath) {
      const roomMatch = lastPath.match(/^rooms\/([^/]+)$/);
      if (roomMatch && roomMatch[1] !== 'overview') {
        try {
          await apiFetch(`/rooms/${roomMatch[1]}`);
          target = `/${lastPath}`;
        } catch {
          target = '/dashboard';
        }
      } else {
        target = `/${lastPath}`;
      }
    } else {
      // Legacy fallback: restore last viewed room — this connection's keys only.
      const last = getHubOwnKeys({ apiUrl })
        .map((key) => localStorage.getItem(`lastViewedRoomsPage:${key}`))
        .find((value): value is string => !!value);
      if (last && last !== 'overview' && /^[a-zA-Z0-9_-]+$/.test(last)) {
        target = `/rooms/${last}`;
      }
    }
  } catch {
    // Storage errors — fall back to the rooms overview
  }
  return target;
}
